import React, { useState, useEffect, useCallback, useMemo } from 'react';
import barcodePrintService from '../../services/barcodePrintService';
import thermalAgentService from '../../services/thermalAgentService';
import masterService from '../../services/masterService';
import ThermalSpecimenLabel from '../../components/common/ThermalSpecimenLabel';
import CommonPagination from '../../components/common/CommonPagination';
import './BarcodeLabelMasterPage.css';

const PREDEFINED_SIZES = [
  {
    id: '50x25',
    label: '50 × 25 mm',
    title: 'Standard Primary Specimen Tube',
    desc: 'Most common lab vacutainer tube (EDTA, Serum, Fluoride, Citrate)',
    icon: '🩸',
    width: 50.0,
    height: 25.0,
    tubeColor: '#a855f7' // Lavender
  },
  {
    id: '50x30',
    label: '50 × 30 mm',
    title: 'Urine & Fluid Specimen Container',
    desc: 'Wider specimen container for 24-hr urine, stool & pleural fluids',
    icon: '🍶',
    width: 50.0,
    height: 30.0,
    tubeColor: '#eab308' // Yellow
  },
  {
    id: '40x20',
    label: '40 × 20 mm',
    title: 'Microbiology Blood Culture & Swabs',
    desc: 'Compact bottle & transport swab label with accession code',
    icon: '🧫',
    width: 40.0,
    height: 20.0,
    tubeColor: '#06b6d4' // Cyan
  },
  {
    id: '75x25',
    label: '75 × 25 mm',
    title: 'Extended Slide & Histology Cassette',
    desc: 'Wider slide label with multiple investigation panels & bi-directional barcodes',
    icon: '🏷️',
    width: 75.0,
    height: 25.0,
    tubeColor: '#64748b'
  },
  {
    id: '25x10',
    label: '25 × 10 mm',
    title: 'Cryovial / Microtube Cap',
    desc: 'Miniature 2D DataMatrix label for deep freeze storage & aliquots',
    icon: '❄️',
    width: 25.0,
    height: 10.0,
    tubeColor: '#3b82f6'
  },
  {
    id: '75x50',
    label: '75 × 50 mm',
    title: 'Specimen Biohazard Transport Bag',
    desc: 'External pouch & courier shipping bag with patient requisition',
    icon: '📦',
    width: 75.0,
    height: 50.0,
    tubeColor: '#ef4444'
  }
];

const SYMBOLOGIES = [
  { value: 'CODE128', label: 'Code 128 (High-Density 1D Linear - Standard)' },
  { value: 'CODE39', label: 'Code 39 (Alphanumeric 1D)' },
  { value: 'QR_CODE', label: 'QR Code (2D High Capacity Matrix)' },
  { value: 'DATAMATRIX', label: 'DataMatrix (2D Compact Medical Matrix)' }
];

// Curated Laboratory Field Library with intuitive labels and icons
const FIELD_DEFINITIONS = [
  { field: 'PATIENT_NAME', label: 'Patient Name', icon: '👤', group: 'Patient', defaultW: 32, defaultH: 4.2, fontSize: 9.5, bold: true, align: 'left', required: true },
  { field: 'UHID', label: 'UHID / Patient ID', icon: '🆔', group: 'Patient', defaultW: 22, defaultH: 3.5, fontSize: 7.5, bold: true, prefix: 'UHID: ', align: 'left' },
  { field: 'AGE_GENDER', label: 'Age & Gender', icon: '⚧', group: 'Patient', defaultW: 16, defaultH: 3.5, fontSize: 7.5, bold: false, align: 'right' },
  { field: 'LAB_NUMBER', label: 'Lab / Work Order No', icon: '🔢', group: 'Sample', defaultW: 22, defaultH: 3.5, fontSize: 7.5, bold: true, prefix: 'Lab: ', align: 'right' },
  { field: 'BARCODE', label: 'Barcode (Code 128 / QR)', icon: '📊', group: 'Barcode', defaultW: 46, defaultH: 7.5, type: 'BARCODE', showText: true, required: true },
  { field: 'TEST_NAME', label: 'Investigation Name', icon: '📋', group: 'Test', defaultW: 30, defaultH: 3.2, fontSize: 7.5, bold: true, align: 'left' },
  { field: 'SAMPLE_TYPE', label: 'Specimen Matrix', icon: '🧪', group: 'Sample', defaultW: 17, defaultH: 3.2, fontSize: 7.5, bold: true, align: 'right' },
  { field: 'CONTAINER_TYPE', label: 'Container / Tube Type', icon: '🩸', group: 'Sample', defaultW: 20, defaultH: 3.2, fontSize: 7.0, bold: false, align: 'left' },
  { field: 'COLLECTION_DATETIME', label: 'Collection Date/Time', icon: '📅', group: 'Sample', defaultW: 24, defaultH: 3.0, fontSize: 6.8, bold: false, align: 'left' },
  { field: 'DOCTOR_NAME', label: 'Referring Doctor', icon: '👨‍⚕️', group: 'Doctor', defaultW: 24, defaultH: 3.0, fontSize: 7.0, bold: false, prefix: 'Dr: ', align: 'left' },
  { field: 'CENTRE_NAME', label: 'Diagnostic Centre Header', icon: '🏥', group: 'System', defaultW: 46, defaultH: 3.5, fontSize: 8.0, bold: true, align: 'center' },
  { field: 'PRIORITY', label: 'STAT / Urgent Urgency Badge', icon: '⚡', group: 'Sample', defaultW: 14, defaultH: 3.5, fontSize: 7.5, bold: true, align: 'right' }
];

// Realistic Sample Testing Data for live visual feedback
const DEMO_SAMPLES = {
  HAEMATOLOGY: {
    patientName: 'MRS. PRIYANKA SHARMA',
    uhid: 'UHID-2026-8910',
    patientId: 'UHID-2026-8910',
    labNo: 'LNO/26/004821',
    ledgerTransactionNo: 'LNO/26/004821',
    barcodeNo: '10984521',
    barcode: '10984521',
    itemName: 'Complete Blood Count (CBC)',
    testName: 'Complete Blood Count (CBC)',
    sampleTypeName: 'Whole Blood (K2-EDTA)',
    containerName: 'Lavender Top (EDTA 3mL)',
    departmentName: 'Haematology',
    centreName: 'AURO DIAGNOSTICS - CENTRAL LAB',
    ageGender: '34Y / Female',
    doctorName: 'Dr. V. K. Aggarwal (MD)',
    collectionDate: '30-Sep-2026',
    collectionTime: '09:45 AM',
    priority: 'ROUTINE'
  },
  BIOCHEMISTRY: {
    patientName: 'MR. RAJESH VERMA',
    uhid: 'UHID-2026-9042',
    patientId: 'UHID-2026-9042',
    labNo: 'LNO/26/004822',
    ledgerTransactionNo: 'LNO/26/004822',
    barcodeNo: '10984522',
    barcode: '10984522',
    itemName: 'Lipid Profile & Liver Function (LFT)',
    testName: 'Lipid Profile & Liver Function (LFT)',
    sampleTypeName: 'Serum (Clot Activator)',
    containerName: 'Gold Top (SST Gel 5mL)',
    departmentName: 'Biochemistry',
    centreName: 'AURO DIAGNOSTICS - CENTRAL LAB',
    ageGender: '48Y / Male',
    doctorName: 'Dr. Neha Gupta (DM)',
    collectionDate: '30-Sep-2026',
    collectionTime: '10:15 AM',
    priority: 'ROUTINE'
  },
  MICROBIOLOGY: {
    patientName: 'BABY OF SUNITA (INFANT)',
    uhid: 'UHID-2026-9115',
    patientId: 'UHID-2026-9115',
    labNo: 'LNO/26/004823',
    ledgerTransactionNo: 'LNO/26/004823',
    barcodeNo: '10984523',
    barcode: '10984523',
    itemName: 'Blood Culture & Sensitivity (Aerobic)',
    testName: 'Blood Culture & Sensitivity (Aerobic)',
    sampleTypeName: 'Blood Culture Broth',
    containerName: 'BD BACTEC Peds Plus Bottle',
    departmentName: 'Microbiology',
    centreName: 'AURO DIAGNOSTICS - CENTRAL LAB',
    ageGender: '11M / Male',
    doctorName: 'Dr. S. K. Mehta (Pediatrics)',
    collectionDate: '30-Sep-2026',
    collectionTime: '11:00 AM',
    priority: 'STAT / URGENT'
  }
};

export const BarcodeLabelMasterPage = () => {
  // Navigation View: 'GRID' | 'DESIGNER' | 'PRINTERS' | 'MAPPINGS' | 'AUDIT'
  const [activeView, setActiveView] = useState('GRID');

  // Wizard Step in Designer: 1: Tube Size, 2: Select Fields, 3: Layout & Preview, 4: Printer & Test
  const [wizardStep, setWizardStep] = useState(1);

  // Master List State
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Masters References
  const [centres, setCentres] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Active Demo Sample for Testing
  const [demoKey, setDemoKey] = useState('HAEMATOLOGY');
  const [previewMode, setPreviewMode] = useState('STICKER'); // 'STICKER' | 'TUBE'

  // Toast Feedback
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Designer State
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [zoom, setZoom] = useState(2.2); // Comfortable default magnification
  const [selectedElementId, setSelectedElementId] = useState(null);

  // Template Form State
  const [formData, setFormData] = useState({
    templateName: 'Standard Primary Specimen Tube (50×25mm)',
    description: 'Universal vacuum blood collection tube label with Code 128 barcode',
    centreId: null,
    departmentId: null,
    sampleTypeId: null,
    containerTypeId: null,
    labelWidthMm: 50.0,
    labelHeightMm: 25.0,
    dpi: 203,
    symbology: 'CODE128',
    barcodeToken: '{BARCODE}',
    includeHumanReadable: true,
    quietZoneMm: 2.0,
    barcodeHeightMm: 7.0,
    orientation: 'LANDSCAPE',
    isActive: true,
    designDataJson: JSON.stringify({
      elements: [
        { id: 'el_centre', field: 'CENTRE_NAME', label: 'Centre Header', x: 2.0, y: 2.0, width: 46.0, height: 3.5, font: 'Arial', fontSize: 8, bold: true, align: 'center', prefix: '', suffix: '' },
        { id: 'el_pname', field: 'PATIENT_NAME', label: 'Patient Name', x: 2.0, y: 5.8, width: 30.0, height: 4.0, font: 'Arial', fontSize: 9.5, bold: true, align: 'left', prefix: '', suffix: '' },
        { id: 'el_age_gender', field: 'AGE_GENDER', label: 'Age & Gender', x: 32.5, y: 5.8, width: 15.5, height: 4.0, font: 'Arial', fontSize: 8, bold: false, align: 'right', prefix: '', suffix: '' },
        { id: 'el_uhid', field: 'UHID', label: 'UHID', x: 2.0, y: 10.0, width: 23.0, height: 3.5, font: 'Consolas', fontSize: 7.5, bold: true, align: 'left', prefix: 'UHID: ', suffix: '' },
        { id: 'el_labno', field: 'LAB_NUMBER', label: 'Lab No', x: 25.5, y: 10.0, width: 22.5, height: 3.5, font: 'Consolas', fontSize: 7.5, bold: true, align: 'right', prefix: 'Lab: ', suffix: '' },
        { id: 'el_bc', field: 'BARCODE', type: 'BARCODE', label: 'Barcode', x: 2.0, y: 13.8, width: 46.0, height: 7.0, showText: true },
        { id: 'el_test', field: 'TEST_NAME', label: 'Investigation Name', x: 2.0, y: 21.2, width: 28.0, height: 3.2, font: 'Arial', fontSize: 7.5, bold: true, align: 'left', prefix: '', suffix: '' },
        { id: 'el_sample', field: 'SAMPLE_TYPE', label: 'Specimen Matrix', x: 30.5, y: 21.2, width: 17.5, height: 3.2, font: 'Arial', fontSize: 7.5, bold: true, align: 'right', prefix: '', suffix: '' }
      ],
      rules: []
    })
  });

  // Modals
  const [versionModalTemplate, setVersionModalTemplate] = useState(null);
  const [versionsList, setVersionsList] = useState([]);
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);

  // Fetch Templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const activeParam = statusFilter === 'ALL' ? null : statusFilter === 'ACTIVE';
      const res = await barcodePrintService.getTemplates({
        search: search.trim() || null,
        isActive: activeParam
      });
      if (res.success && Array.isArray(res.data)) {
        setTemplates(res.data);
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
      showToast('Error loading templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  const fetchDropdownMasters = useCallback(async () => {
    try {
      const [cRes, dRes, pRes] = await Promise.all([
        masterService.getCentres().catch(() => ({ data: [] })),
        masterService.getDepartments().catch(() => ({ data: [] })),
        barcodePrintService.getPrinters().catch(() => ({ data: [] }))
      ]);
      if (cRes.data) setCentres(cRes.data);
      if (dRes.data) setDepartments(dRes.data);
      if (pRes.data) setPrinters(pRes.data);
    } catch (e) {
      console.warn('Dropdown masters partial fetch:', e);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchDropdownMasters();
  }, [fetchTemplates, fetchDropdownMasters]);

  // Elements Array
  const elements = useMemo(() => {
    try {
      const parsed = JSON.parse(formData.designDataJson || '{}');
      return parsed.elements || [];
    } catch {
      return [];
    }
  }, [formData.designDataJson]);

  const updateElements = (newElements) => {
    try {
      const parsed = JSON.parse(formData.designDataJson || '{}');
      parsed.elements = newElements;
      setFormData((prev) => ({ ...prev, designDataJson: JSON.stringify(parsed) }));
    } catch {
      setFormData((prev) => ({ ...prev, designDataJson: JSON.stringify({ elements: newElements }) }));
    }
  };

  // Toggle field on/off in Step 2 with auto-calculated non-overlapping layout
  const handleToggleField = (fieldDef) => {
    const exists = elements.some((el) => el.field === fieldDef.field);
    if (exists) {
      if (fieldDef.required) {
        showToast(`${fieldDef.label} is required for clinical identification`, 'warning');
        return;
      }
      const nextList = elements.filter((el) => el.field !== fieldDef.field);
      updateElements(nextList);
      if (selectedElementId && elements.find((e) => e.id === selectedElementId)?.field === fieldDef.field) {
        setSelectedElementId(null);
      }
      showToast(`Removed ${fieldDef.label}`, 'info');
    } else {
      // Auto-assign clean position based on field type
      let defaultY = 21.0;
      let defaultX = 2.0;
      if (fieldDef.field === 'CENTRE_NAME') { defaultX = 2.0; defaultY = 2.0; }
      else if (fieldDef.field === 'PATIENT_NAME') { defaultX = 2.0; defaultY = 5.8; }
      else if (fieldDef.field === 'AGE_GENDER') { defaultX = 32.5; defaultY = 5.8; }
      else if (fieldDef.field === 'UHID') { defaultX = 2.0; defaultY = 10.0; }
      else if (fieldDef.field === 'LAB_NUMBER') { defaultX = 25.5; defaultY = 10.0; }
      else if (fieldDef.field === 'BARCODE') { defaultX = 2.0; defaultY = 13.8; }
      else if (fieldDef.field === 'TEST_NAME') { defaultX = 2.0; defaultY = 21.2; }
      else if (fieldDef.field === 'SAMPLE_TYPE') { defaultX = 30.5; defaultY = 21.2; }

      const newEl = {
        id: `el_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
        field: fieldDef.field,
        type: fieldDef.type || 'TEXT',
        label: fieldDef.label,
        x: defaultX,
        y: defaultY,
        width: fieldDef.defaultW || 24.0,
        height: fieldDef.defaultH || 3.5,
        font: 'Arial',
        fontSize: fieldDef.fontSize || 8,
        bold: Boolean(fieldDef.bold),
        italic: false,
        align: fieldDef.align || 'left',
        prefix: fieldDef.prefix || '',
        suffix: fieldDef.suffix || '',
        showText: fieldDef.showText
      };
      const nextList = [...elements, newEl];
      updateElements(nextList);
      setSelectedElementId(newEl.id);
      showToast(`Added ${fieldDef.label} to label`, 'success');
    }
  };

  // 1-Click Clinical Layout Presets
  const applyPreset = (presetType) => {
    let presetElements = [];
    if (presetType === 'HAEMATOLOGY') {
      presetElements = [
        { id: 'el_centre', field: 'CENTRE_NAME', label: 'Centre Header', x: 2.0, y: 2.0, width: 46.0, height: 3.5, font: 'Arial', fontSize: 8, bold: true, align: 'center', prefix: '' },
        { id: 'el_pname', field: 'PATIENT_NAME', label: 'Patient Name', x: 2.0, y: 5.8, width: 30.0, height: 4.0, font: 'Arial', fontSize: 9.5, bold: true, align: 'left', prefix: '' },
        { id: 'el_age_gender', field: 'AGE_GENDER', label: 'Age & Gender', x: 32.5, y: 5.8, width: 15.5, height: 4.0, font: 'Arial', fontSize: 8, bold: false, align: 'right', prefix: '' },
        { id: 'el_uhid', field: 'UHID', label: 'UHID', x: 2.0, y: 10.0, width: 23.0, height: 3.5, font: 'Consolas', fontSize: 7.5, bold: true, align: 'left', prefix: 'UHID: ' },
        { id: 'el_labno', field: 'LAB_NUMBER', label: 'Lab No', x: 25.5, y: 10.0, width: 22.5, height: 3.5, font: 'Consolas', fontSize: 7.5, bold: true, align: 'right', prefix: 'Lab: ' },
        { id: 'el_bc', field: 'BARCODE', type: 'BARCODE', label: 'Barcode', x: 2.0, y: 13.8, width: 46.0, height: 7.0, showText: true },
        { id: 'el_test', field: 'TEST_NAME', label: 'Test Name', x: 2.0, y: 21.2, width: 28.0, height: 3.2, font: 'Arial', fontSize: 7.5, bold: true, align: 'left', prefix: '' },
        { id: 'el_sample', field: 'SAMPLE_TYPE', label: 'Specimen Matrix', x: 30.5, y: 21.2, width: 17.5, height: 3.2, font: 'Arial', fontSize: 7.5, bold: true, align: 'right', prefix: '' }
      ];
      setFormData((prev) => ({
        ...prev,
        labelWidthMm: 50.0,
        labelHeightMm: 25.0,
        symbology: 'CODE128',
        templateName: 'Standard Primary Blood Tube (50×25mm)'
      }));
    } else if (presetType === 'MICROBIOLOGY') {
      presetElements = [
        { id: 'el_labno', field: 'LAB_NUMBER', label: 'Accession / Lab No', x: 2.0, y: 2.5, width: 24.0, height: 4.0, font: 'Consolas', fontSize: 9.0, bold: true, align: 'left', prefix: 'ACC: ' },
        { id: 'el_pname', field: 'PATIENT_NAME', label: 'Patient Name', x: 2.0, y: 6.8, width: 34.0, height: 4.0, font: 'Arial', fontSize: 9.0, bold: true, align: 'left' },
        { id: 'el_bc', field: 'BARCODE', type: 'BARCODE', label: 'Barcode', x: 2.0, y: 11.2, width: 36.0, height: 8.0, showText: true },
        { id: 'el_test', field: 'TEST_NAME', label: 'Culture & Swab', x: 2.0, y: 19.8, width: 26.0, height: 3.5, font: 'Arial', fontSize: 7.5, bold: true, align: 'left' },
        { id: 'el_sample', field: 'SAMPLE_TYPE', label: 'Specimen Site', x: 28.0, y: 19.8, width: 11.0, height: 3.5, font: 'Arial', fontSize: 7.5, bold: true, align: 'right' }
      ];
      setFormData((prev) => ({
        ...prev,
        labelWidthMm: 40.0,
        labelHeightMm: 20.0,
        symbology: 'CODE128',
        templateName: 'Microbiology Culture Swab Label (40×20mm)'
      }));
    } else if (presetType === 'URINE_FLUID') {
      presetElements = [
        { id: 'el_centre', field: 'CENTRE_NAME', label: 'Centre Header', x: 2.0, y: 2.0, width: 46.0, height: 3.5, font: 'Arial', fontSize: 8, bold: true, align: 'center' },
        { id: 'el_pname', field: 'PATIENT_NAME', label: 'Patient Name', x: 2.0, y: 6.0, width: 32.0, height: 4.5, font: 'Arial', fontSize: 10.0, bold: true, align: 'left' },
        { id: 'el_uhid', field: 'UHID', label: 'UHID', x: 2.0, y: 11.0, width: 22.0, height: 3.5, font: 'Consolas', fontSize: 8.0, bold: true, align: 'left', prefix: 'UHID: ' },
        { id: 'el_labno', field: 'LAB_NUMBER', label: 'Lab No', x: 25.0, y: 11.0, width: 23.0, height: 3.5, font: 'Consolas', fontSize: 8.0, bold: true, align: 'right', prefix: 'Lab: ' },
        { id: 'el_bc', field: 'BARCODE', type: 'BARCODE', label: '2D QR Code', x: 2.0, y: 15.0, width: 46.0, height: 9.0, showText: true },
        { id: 'el_test', field: 'TEST_NAME', label: 'Fluid / Urine Test', x: 2.0, y: 25.0, width: 28.0, height: 3.5, font: 'Arial', fontSize: 8.0, bold: true, align: 'left' },
        { id: 'el_sample', field: 'SAMPLE_TYPE', label: 'Container Preservative', x: 30.0, y: 25.0, width: 18.0, height: 3.5, font: 'Arial', fontSize: 8.0, bold: true, align: 'right' }
      ];
      setFormData((prev) => ({
        ...prev,
        labelWidthMm: 50.0,
        labelHeightMm: 30.0,
        symbology: 'QR_CODE',
        templateName: 'Urine & Fluid Specimen Container (50×30mm)'
      }));
    }
    updateElements(presetElements);
    showToast(`Applied ${presetType} best-practice layout preset!`, 'success');
  };

  // Save Template Action
  const handleSaveTemplate = async () => {
    if (!formData.templateName.trim()) {
      showToast('Template Name is required', 'warning');
      return;
    }
    setLoading(true);
    try {
      if (editingTemplateId) {
        const res = await barcodePrintService.updateTemplate(editingTemplateId, {
          ...formData,
          templateId: editingTemplateId,
          changeReason: 'Updated via Visual Template Designer'
        });
        if (res.success) {
          showToast('✓ Template updated and new version snapshot saved successfully!', 'success');
          setActiveView('GRID');
          fetchTemplates();
        } else {
          showToast(res.message || 'Failed to update template', 'error');
        }
      } else {
        const res = await barcodePrintService.createTemplate(formData);
        if (res.success) {
          showToast('✓ New Barcode Label Template created successfully!', 'success');
          setActiveView('GRID');
          fetchTemplates();
        } else {
          showToast(res.message || 'Failed to create template', 'error');
        }
      }
    } catch (err) {
      console.error('Save template error:', err);
      showToast(err.response?.data?.message || 'Error saving template', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Test Print Execution
  const handleTestPrint = async () => {
    setLoading(true);
    try {
      const activePrinter = printers[0];
      const res = await barcodePrintService.generateThermalPayload({
        templateId: editingTemplateId || 1,
        printerId: activePrinter?.printerId || 1,
        protocol: activePrinter?.protocol || 'ZPL',
        sampleData: DEMO_SAMPLES[demoKey],
        copies: 1
      });

      if (res.success && res.data?.rawPayload) {
        // Dispatch to local agent
        const agentRes = await thermalAgentService.sendRawThermalJob({
          printerName: activePrinter?.windowsPrinterName || activePrinter?.printerName || 'Thermal Printer',
          protocol: activePrinter?.protocol || 'ZPL',
          rawPayload: res.data.rawPayload,
          copies: 1,
          jobTitle: `Test Print - ${formData.templateName}`
        });

        if (agentRes.success) {
          showToast(`✓ Test label dispatched to ${activePrinter?.printerName || 'Thermal Printer'}!`, 'success');
        } else {
          showToast('Local Print Agent offline. Generating precision browser print...', 'warning');
          window.print();
        }
      }
    } catch (err) {
      showToast('Printing test label failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Save Thermal Printer Registration
  const handleSavePrinter = async (e) => {
    if (e) e.preventDefault();
    if (!editingPrinter?.printerName?.trim()) {
      showToast('Printer Name is required', 'warning');
      return;
    }
    setLoading(true);
    try {
      const res = await barcodePrintService.savePrinter(editingPrinter);
      if (res?.success) {
        showToast('✓ Thermal printer registered successfully!', 'success');
        setShowPrinterModal(false);
        const pRes = await barcodePrintService.getPrinters();
        if (pRes?.data) setPrinters(pRes.data);
      } else {
        showToast(res?.message || 'Failed to save printer', 'error');
      }
    } catch (err) {
      console.error('Save printer error:', err);
      showToast('Error saving printer configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectedElement = useMemo(() => {
    return elements.find((el) => el.id === selectedElementId) || null;
  }, [elements, selectedElementId]);

  return (
    <div className="blm-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`blm-toast blm-toast-${toast.type}`}>
          <span>{toast.type === 'success' ? '✓' : '⚠️'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Main Top Header */}
      <header className="blm-header">
        <div className="blm-header-left">
          <span className="blm-header-icon">🏷️</span>
          <div>
            <h1 className="blm-title">Barcode Label & Thermal Print Master</h1>
            <p className="blm-desc">
              Master-Driven Specimen Tube Label Templates • Thermal Printing Protocols & Zero-Technician Resolution
            </p>
          </div>
        </div>

        <div className="blm-tab-bar">
          <button
            type="button"
            className={`blm-tab ${activeView === 'GRID' ? 'active' : ''}`}
            onClick={() => setActiveView('GRID')}
          >
            📋 Label Templates
          </button>
          <button
            type="button"
            className={`blm-tab ${activeView === 'PRINTERS' ? 'active' : ''}`}
            onClick={async () => {
              setActiveView('PRINTERS');
              try {
                const res = await barcodePrintService.getPrinters();
                if (res?.data) setPrinters(res.data);
              } catch (err) {
                console.error('Failed to load printers:', err);
                showToast('Unable to load thermal printers', 'error');
              }
            }}
          >
            🖨️ Thermal Printers
          </button>
          <button
            type="button"
            className={`blm-tab ${activeView === 'MAPPINGS' ? 'active' : ''}`}
            onClick={async () => {
              setActiveView('MAPPINGS');
              try {
                const res = await barcodePrintService.getMappings();
                if (res?.data) setMappings(res.data);
              } catch (err) {
                console.error('Failed to load mappings:', err);
                showToast('Unable to load printer mappings', 'error');
              }
            }}
          >
            🔀 Printer Mappings
          </button>
          <button
            type="button"
            className={`blm-tab ${activeView === 'AUDIT' ? 'active' : ''}`}
            onClick={async () => {
              setActiveView('AUDIT');
              try {
                const res = await barcodePrintService.getPrintJobs();
                if (res?.data) setAuditLogs(res.data);
              } catch (err) {
                console.error('Failed to load print audit:', err);
                showToast('Unable to load print audit history', 'error');
              }
            }}
          >
            📜 Print Job Audit
          </button>
        </div>
      </header>

      {/* ========================================================================= */}
      {/* VIEW 1: TEMPLATES GRID                                                    */}
      {/* ========================================================================= */}
      {activeView === 'GRID' && (
        <section className="blm-content-section">
          {/* Quick Helper Banner */}
          <div className="blm-help-banner">
            <div className="blm-help-icon">💡</div>
            <div className="blm-help-text">
              <strong>Master-Driven Printing Rule:</strong> Phlebotomists & lab technicians never configure labels during collection. 
              Configure your laboratory tube sizes and printer mappings here once — the system will automatically resolve the exact template and thermal printer for every specimen.
            </div>
            <button
              type="button"
              className="blm-btn-primary"
              onClick={() => {
                setEditingTemplateId(null);
                setWizardStep(1);
                applyPreset('HAEMATOLOGY');
                setActiveView('DESIGNER');
              }}
            >
              + Create New Tube Label
            </button>
          </div>

          {/* Controls Bar */}
          <div className="blm-controls-bar">
            <div className="blm-search-wrap">
              <span className="blm-search-icon">🔍</span>
              <input
                type="text"
                className="blm-search-input"
                placeholder="Search label templates by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="blm-filter-group">
              <select
                className="blm-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">All Statuses</option>
                <option value="ACTIVE">Active Only</option>
                <option value="INACTIVE">Inactive Only</option>
              </select>
            </div>
          </div>

          {/* Templates Grid Table */}
          <div className="blm-table-wrap">
            <table className="blm-table">
              <thead>
                <tr>
                  <th style={{ width: '45px' }}>#</th>
                  <th>Template Name</th>
                  <th>Tube Size</th>
                  <th>Symbology</th>
                  <th>Department / Center</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Modified</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="blm-empty-td">Loading label templates...</td>
                  </tr>
                ) : templates.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="blm-empty-td">No templates found. Click "+ Create New Tube Label" to configure your first label.</td>
                  </tr>
                ) : (
                  templates.slice((page - 1) * pageSize, page * pageSize).map((t, idx) => (
                    <tr key={t.templateId}>
                      <td className="font-mono text-muted">{(page - 1) * pageSize + idx + 1}</td>
                      <td>
                        <strong className="blm-tname">{t.templateName}</strong>
                        {t.description && <span className="blm-tdesc">{t.description}</span>}
                      </td>
                      <td>
                        <span className="blm-size-pill">
                          {t.labelWidthMm} × {t.labelHeightMm} mm
                        </span>
                        <span className="blm-dpi-sub">{t.dpi} DPI</span>
                      </td>
                      <td>
                        <span className="blm-symbology-badge">{t.symbology}</span>
                      </td>
                      <td>
                        <div className="blm-scope-cell">
                          <span>{t.departmentName || 'All Departments'}</span>
                          <span className="text-muted text-xs">{t.centreName || 'Universal All Centres'}</span>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="blm-btn-version-link"
                          onClick={() => {
                            setVersionModalTemplate(t);
                            barcodePrintService.getTemplateVersions(t.templateId).then((res) => {
                              if (res.data) setVersionsList(res.data);
                            });
                          }}
                          title="View version history snapshot"
                        >
                          v{t.version} 📜
                        </button>
                      </td>
                      <td>
                        <span className={`blm-badge ${t.isActive ? 'badge-active' : 'badge-inactive'}`}>
                          {t.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="text-xs text-muted">
                        {t.modifiedDate ? new Date(t.modifiedDate).toLocaleDateString() : new Date(t.createdDate).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="blm-actions-cell">
                          <button
                            type="button"
                            className="blm-btn-row-edit"
                            onClick={() => {
                              setEditingTemplateId(t.templateId);
                              setFormData({
                                templateName: t.templateName,
                                description: t.description || '',
                                centreId: t.centreId,
                                departmentId: t.departmentId,
                                sampleTypeId: t.sampleTypeId,
                                containerTypeId: t.containerTypeId,
                                labelWidthMm: t.labelWidthMm,
                                labelHeightMm: t.labelHeightMm,
                                dpi: t.dpi,
                                symbology: t.symbology,
                                barcodeToken: t.barcodeToken,
                                includeHumanReadable: t.includeHumanReadable,
                                quietZoneMm: t.quietZoneMm,
                                barcodeHeightMm: t.barcodeHeightMm,
                                orientation: t.orientation || 'LANDSCAPE',
                                isActive: t.isActive,
                                designDataJson: t.designDataJson
                              });
                              setWizardStep(3); // Jump straight to preview/layout when editing
                              setActiveView('DESIGNER');
                            }}
                          >
                            ✏️ Edit Design
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="blm-pagination-bar">
            <CommonPagination
              page={page}
              pageSize={pageSize}
              totalRecords={templates.length}
              onPageChange={setPage}
            />
          </div>
        </section>
      )}

      {/* ========================================================================= */}
      {/* VIEW 2: GUIDED 4-STEP VISUAL LABEL DESIGNER WIZARD                        */}
      {/* ========================================================================= */}
      {activeView === 'DESIGNER' && (
        <section className="blm-wizard-container">
          {/* Top Wizard Steps Header */}
          <div className="blm-wizard-nav">
            <button
              type="button"
              className="blm-btn-back"
              onClick={() => setActiveView('GRID')}
            >
              ← Back to Templates
            </button>

            <div className="blm-wizard-steps">
              <button
                type="button"
                className={`blm-step-pill ${wizardStep === 1 ? 'active' : wizardStep > 1 ? 'completed' : ''}`}
                onClick={() => setWizardStep(1)}
              >
                <span className="blm-step-num">{wizardStep > 1 ? '✓' : '1'}</span>
                <span>1. Tube Size & Name</span>
              </button>

              <span className="blm-step-arrow">➔</span>

              <button
                type="button"
                className={`blm-step-pill ${wizardStep === 2 ? 'active' : wizardStep > 2 ? 'completed' : ''}`}
                onClick={() => setWizardStep(2)}
              >
                <span className="blm-step-num">{wizardStep > 2 ? '✓' : '2'}</span>
                <span>2. Select Fields</span>
              </button>

              <span className="blm-step-arrow">➔</span>

              <button
                type="button"
                className={`blm-step-pill ${wizardStep === 3 ? 'active' : wizardStep > 3 ? 'completed' : ''}`}
                onClick={() => setWizardStep(3)}
              >
                <span className="blm-step-num">{wizardStep > 3 ? '✓' : '3'}</span>
                <span>3. Live Preview & Layout</span>
              </button>

              <span className="blm-step-arrow">➔</span>

              <button
                type="button"
                className={`blm-step-pill ${wizardStep === 4 ? 'active' : ''}`}
                onClick={() => setWizardStep(4)}
              >
                <span className="blm-step-num">4</span>
                <span>4. Printer & Test</span>
              </button>
            </div>

            <div className="blm-wizard-actions">
              <button
                type="button"
                className="blm-btn-save-primary"
                onClick={handleSaveTemplate}
                disabled={loading}
              >
                {loading ? 'Saving...' : '💾 Save Template (Bump Version)'}
              </button>
            </div>
          </div>

          {/* WIZARD STEP 1: TUBE SIZE & SCOPE */}
          {wizardStep === 1 && (
            <div className="blm-step-pane">
              <div className="blm-step-header">
                <h2>Step 1: Choose Tube Size & Label Scope</h2>
                <p>Select your physical laboratory tube or container dimensions. The label canvas will scale accordingly.</p>
              </div>

              {/* Template Name & Scope */}
              <div className="blm-form-card">
                <div className="blm-form-row">
                  <div style={{ flex: 2 }}>
                    <label className="blm-field-label">Template Display Name <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="text"
                      className="blm-text-input"
                      value={formData.templateName}
                      onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                      placeholder="e.g. Standard Primary Blood Tube (50×25mm)"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="blm-field-label">Department Scope</label>
                    <select
                      className="blm-select-full"
                      value={formData.departmentId || ''}
                      onChange={(e) => setFormData({ ...formData, departmentId: e.target.value ? parseInt(e.target.value, 10) : null })}
                    >
                      <option value="">Universal (All Departments)</option>
                      {departments.map((d) => (
                        <option key={d.departmentID} value={d.departmentID}>{d.departmentName}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="blm-field-label">Printer Resolution (DPI)</label>
                    <select
                      className="blm-select-full"
                      value={formData.dpi}
                      onChange={(e) => setFormData({ ...formData, dpi: parseInt(e.target.value, 10) })}
                    >
                      <option value={203}>203 DPI (8 dots/mm - Standard Thermal)</option>
                      <option value={300}>300 DPI (12 dots/mm - High Definition)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Physical Tube Size Selection Cards */}
              <div className="blm-size-cards-grid">
                {PREDEFINED_SIZES.map((size) => {
                  const isSelected = formData.labelWidthMm === size.width && formData.labelHeightMm === size.height;
                  return (
                    <div
                      key={size.id}
                      className={`blm-size-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, labelWidthMm: size.width, labelHeightMm: size.height })}
                    >
                      <div className="blm-size-card-top">
                        <span className="blm-size-icon">{size.icon}</span>
                        <span className="blm-size-dim-badge">{size.label}</span>
                      </div>
                      <h4 className="blm-size-title">{size.title}</h4>
                      <p className="blm-size-desc">{size.desc}</p>
                      <div className="blm-size-footer">
                        {isSelected ? <span className="blm-selected-check">✓ Active Size</span> : <span className="blm-select-link">Click to select</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="blm-step-footer">
                <div />
                <button
                  type="button"
                  className="blm-btn-next"
                  onClick={() => setWizardStep(2)}
                >
                  Next: Select Fields to Print →
                </button>
              </div>
            </div>
          )}

          {/* WIZARD STEP 2: FIELD SELECTION TOGGLE CHIPS */}
          {wizardStep === 2 && (
            <div className="blm-step-pane">
              <div className="blm-step-header">
                <h2>Step 2: Choose Information to Print</h2>
                <p>Click on the fields you want to appear on the tube label. We automatically arrange them with clean non-overlapping spacing.</p>
              </div>

              <div className="blm-fields-toggle-grid">
                {FIELD_DEFINITIONS.map((f) => {
                  const isEnabled = elements.some((el) => el.field === f.field);
                  return (
                    <div
                      key={f.field}
                      className={`blm-field-toggle-card ${isEnabled ? 'enabled' : ''}`}
                      onClick={() => handleToggleField(f)}
                    >
                      <div className="blm-ftc-left">
                        <span className="blm-ftc-icon">{f.icon}</span>
                        <div>
                          <strong className="blm-ftc-name">{f.label}</strong>
                          <span className="blm-ftc-group">{f.group} Field</span>
                        </div>
                      </div>
                      <div className="blm-ftc-right">
                        <span className={`blm-switch ${isEnabled ? 'on' : 'off'}`}>
                          <span className="blm-switch-handle" />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="blm-step-footer">
                <button
                  type="button"
                  className="blm-btn-prev"
                  onClick={() => setWizardStep(1)}
                >
                  ← Back to Tube Size
                </button>
                <button
                  type="button"
                  className="blm-btn-next"
                  onClick={() => setWizardStep(3)}
                >
                  Next: Visual Layout & Preview →
                </button>
              </div>
            </div>
          )}

          {/* WIZARD STEP 3: LIVE PREVIEW & VISUAL FINE-TUNING */}
          {wizardStep === 3 && (
            <div className="blm-step-pane-designer">
              {/* Presets Bar */}
              <div className="blm-presets-bar">
                <span className="blm-preset-label">⚡ 1-Click Clinical Presets:</span>
                <button type="button" onClick={() => applyPreset('HAEMATOLOGY')} className="blm-preset-btn">
                  🩸 Standard Blood Tube (50×25mm)
                </button>
                <button type="button" onClick={() => applyPreset('MICROBIOLOGY')} className="blm-preset-btn">
                  🧫 Microbiology Culture (40×20mm)
                </button>
                <button type="button" onClick={() => applyPreset('URINE_FLUID')} className="blm-preset-btn">
                  🍶 Urine & Fluid (50×30mm QR)
                </button>

                <div className="blm-preview-mode-switch">
                  <button
                    type="button"
                    className={previewMode === 'STICKER' ? 'active' : ''}
                    onClick={() => setPreviewMode('STICKER')}
                  >
                    🏷️ Sticker View
                  </button>
                  <button
                    type="button"
                    className={previewMode === 'TUBE' ? 'active' : ''}
                    onClick={() => setPreviewMode('TUBE')}
                  >
                    🧪 Real Tube Simulation
                  </button>
                </div>
              </div>

              {/* 2-Column Stage: Left Magnified Center Canvas + Right Clean Properties */}
              <div className="blm-stage-layout">
                {/* Center Canvas Stage */}
                <div className="blm-main-canvas-area">
                  <div className="blm-canvas-card">
                    {/* Top Stage Controls */}
                    <div className="blm-stage-controls">
                      <div className="blm-demo-picker">
                        <span>Test Sample:</span>
                        <select value={demoKey} onChange={(e) => setDemoKey(e.target.value)} className="blm-demo-select">
                          <option value="HAEMATOLOGY">Complete Blood Count (CBC - Lavender Tube)</option>
                          <option value="BIOCHEMISTRY">Lipid & LFT (Serum SST - Gold Tube)</option>
                          <option value="MICROBIOLOGY">Blood Culture (Aerobic Swab Bottle)</option>
                        </select>
                      </div>

                      <div className="blm-zoom-pill">
                        <button type="button" onClick={() => setZoom(1.5)} className={zoom === 1.5 ? 'active' : ''}>150%</button>
                        <button type="button" onClick={() => setZoom(2.2)} className={zoom === 2.2 ? 'active' : ''}>220% (Optimal)</button>
                        <button type="button" onClick={() => setZoom(2.8)} className={zoom === 2.8 ? 'active' : ''}>280% (Zoom)</button>
                      </div>
                    </div>

                    {/* Stage Presentation */}
                    <div className="blm-presentation-stage">
                      {previewMode === 'TUBE' ? (
                        <div className="blm-tube-mockup">
                          {/* Simulated Vacutainer Rubber Stopper */}
                          <div className="blm-tube-cap" style={{ background: demoKey === 'HAEMATOLOGY' ? '#9333ea' : demoKey === 'BIOCHEMISTRY' ? '#eab308' : '#0284c7' }}>
                            <span className="blm-tube-rubber-dimple" />
                          </div>
                          {/* Glass Tube Body */}
                          <div className="blm-tube-glass">
                            <div className="blm-tube-label-wrap">
                              <ThermalSpecimenLabel
                                template={formData}
                                sampleData={DEMO_SAMPLES[demoKey]}
                                zoom={1.6}
                                isInteractive={true}
                                selectedElementId={selectedElementId}
                                onSelectElement={(el) => setSelectedElementId(el.id)}
                              />
                            </div>
                            <div className="blm-tube-blood-fill" style={{ background: demoKey === 'BIOCHEMISTRY' ? '#f59e0b' : '#991b1b' }} />
                          </div>
                        </div>
                      ) : (
                        <div className="blm-sticker-board">
                          <ThermalSpecimenLabel
                            template={formData}
                            sampleData={DEMO_SAMPLES[demoKey]}
                            zoom={zoom}
                            isInteractive={true}
                            selectedElementId={selectedElementId}
                            onSelectElement={(el) => setSelectedElementId(el.id)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Properties Panel */}
                <div className="blm-side-properties-area">
                  <div className="blm-prop-box">
                    <h4 className="blm-prop-title">Element Settings</h4>

                    {selectedElement ? (
                      <div className="blm-clean-prop-form">
                        <div className="blm-clean-prop-header">
                          <strong className="text-sm font-bold text-slate-800">{selectedElement.label || selectedElement.field}</strong>
                          <button
                            type="button"
                            className="blm-btn-del"
                            onClick={() => {
                              const next = elements.filter((e) => e.id !== selectedElement.id);
                              updateElements(next);
                              setSelectedElementId(null);
                            }}
                          >
                            🗑️ Remove
                          </button>
                        </div>

                        {selectedElement.type !== 'BARCODE' && (
                          <>
                            <div className="blm-prop-item">
                              <label className="text-xs font-semibold text-slate-600">Font Size</label>
                              <div className="blm-font-size-buttons">
                                {[7, 8, 9.5, 11].map((s) => (
                                  <button
                                    key={s}
                                    type="button"
                                    className={`blm-fbtn ${selectedElement.fontSize === s ? 'active' : ''}`}
                                    onClick={() => {
                                      const next = elements.map((e) => e.id === selectedElement.id ? { ...e, fontSize: s } : e);
                                      updateElements(next);
                                    }}
                                  >
                                    {s}pt
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="blm-prop-item">
                              <label className="text-xs font-semibold text-slate-600">Prefix Text</label>
                              <input
                                type="text"
                                className="blm-text-input"
                                value={selectedElement.prefix || ''}
                                onChange={(e) => {
                                  const next = elements.map((el) => el.id === selectedElement.id ? { ...el, prefix: e.target.value } : el);
                                  updateElements(next);
                                }}
                                placeholder="e.g. UHID: or Lab: "
                              />
                            </div>

                            <div className="blm-prop-item">
                              <label className="text-xs font-semibold text-slate-600">Text Alignment</label>
                              <div className="blm-align-button-group">
                                {['left', 'center', 'right'].map((al) => (
                                  <button
                                    key={al}
                                    type="button"
                                    className={`blm-abtn ${selectedElement.align === al ? 'active' : ''}`}
                                    onClick={() => {
                                      const next = elements.map((e) => e.id === selectedElement.id ? { ...e, align: al } : e);
                                      updateElements(next);
                                    }}
                                  >
                                    {al.toUpperCase()}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="blm-format-switches">
                              <label className="blm-format-label">
                                <input
                                  type="checkbox"
                                  checked={Boolean(selectedElement.bold)}
                                  onChange={(e) => {
                                    const next = elements.map((el) => el.id === selectedElement.id ? { ...el, bold: e.target.checked } : el);
                                    updateElements(next);
                                  }}
                                />
                                <span>Bold Text</span>
                              </label>
                            </div>
                          </>
                        )}

                        {selectedElement.type === 'BARCODE' && (
                          <div className="blm-prop-item">
                            <label className="text-xs font-semibold text-slate-600">Symbology Format</label>
                            <select
                              className="blm-select-full"
                              value={formData.symbology}
                              onChange={(e) => setFormData({ ...formData, symbology: e.target.value })}
                            >
                              {SYMBOLOGIES.map((s, i) => (
                                <option key={i} value={s.value}>{s.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="blm-empty-prop-notice">
                        <span className="text-3xl">👆</span>
                        <p>Click on any text or barcode on the label to adjust its font size, alignment, or prefix.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="blm-step-footer">
                <button
                  type="button"
                  className="blm-btn-prev"
                  onClick={() => setWizardStep(2)}
                >
                  ← Back to Fields
                </button>
                <button
                  type="button"
                  className="blm-btn-next"
                  onClick={() => setWizardStep(4)}
                >
                  Next: Printer Setup & Test Print →
                </button>
              </div>
            </div>
          )}

          {/* WIZARD STEP 4: PRINTER MAPPING & LIVE TEST */}
          {wizardStep === 4 && (
            <div className="blm-step-pane">
              <div className="blm-step-header">
                <h2>Step 4: Mapped Thermal Printer & Verification</h2>
                <p>Verify that your thermal printer is ready and dispatch a live test label before activating this template.</p>
              </div>

              <div className="blm-step4-grid">
                <div className="blm-step4-card">
                  <h4 className="font-bold text-slate-800 mb-2">Target Thermal Hardware</h4>
                  <div className="blm-printer-choice-box">
                    <span className="text-2xl">🖨️</span>
                    <div>
                      <strong>{printers[0]?.printerName || 'Central Lab Zebra ZD220 (Primary)'}</strong>
                      <p className="text-xs text-slate-500">
                        Protocol: {printers[0]?.protocol || 'ZPL'} • Connection: {printers[0]?.connectionType || 'Local Print Agent'}
                      </p>
                    </div>
                    <span className="blm-badge badge-active ml-auto">READY 🟢</span>
                  </div>

                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <h5 className="font-bold text-xs uppercase text-slate-600 mb-1">Automated Resolution Rule</h5>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Whenever a specimen matching <strong>{formData.templateName}</strong> is collected or received, 
                      the system will automatically generate native <strong>{printers[0]?.protocol || 'ZPL'}</strong> thermal code 
                      and dispatch it directly to this printer with zero technician prompt.
                    </p>
                  </div>

                  <div className="mt-6 flex gap-3">
                    <button
                      type="button"
                      className="blm-btn-test-print"
                      onClick={handleTestPrint}
                      disabled={loading}
                    >
                      ⚡ Run Test Print on Thermal Printer
                    </button>
                  </div>
                </div>

                <div className="blm-step4-card">
                  <h4 className="font-bold text-slate-800 mb-2">Final Summary & Verification</h4>
                  <div className="blm-final-summary-list">
                    <div className="blm-sum-row">
                      <span>Template:</span>
                      <strong>{formData.templateName}</strong>
                    </div>
                    <div className="blm-sum-row">
                      <span>Physical Dimensions:</span>
                      <strong>{formData.labelWidthMm} × {formData.labelHeightMm} mm ({formData.dpi} DPI)</strong>
                    </div>
                    <div className="blm-sum-row">
                      <span>Barcode Symbology:</span>
                      <strong>{formData.symbology}</strong>
                    </div>
                    <div className="blm-sum-row">
                      <span>Total Active Fields:</span>
                      <strong>{elements.length} fields</strong>
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      type="button"
                      className="blm-btn-save-final"
                      onClick={handleSaveTemplate}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : '✓ Confirm & Activate Label Template'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="blm-step-footer">
                <button
                  type="button"
                  className="blm-btn-prev"
                  onClick={() => setWizardStep(3)}
                >
                  ← Back to Preview
                </button>
                <div />
              </div>
            </div>
          )}
        </section>
      )}

      {/* ========================================================================= */}
      {/* VIEW 3: THERMAL PRINTERS MASTER                                           */}
      {/* ========================================================================= */}
      {activeView === 'PRINTERS' && (
        <section className="blm-content-section">
          <div className="blm-controls-bar">
            <h3 className="font-bold text-base">Registered Thermal Printers</h3>
            <button
              type="button"
              className="blm-btn-primary"
              onClick={() => {
                setEditingPrinter({
                  printerName: '',
                  manufacturer: 'Zebra',
                  model: 'ZD220',
                  connectionType: 'LOCAL_AGENT',
                  protocol: 'ZPL',
                  ipAddress: '127.0.0.1',
                  port: 9100,
                  windowsPrinterName: 'ZDesigner ZD220-203dpi ZPL',
                  dpi: 203,
                  status: 'READY',
                  isDefault: false,
                  isActive: true
                });
                setShowPrinterModal(true);
              }}
            >
              + Register Thermal Printer
            </button>
          </div>

          <div className="blm-table-wrap">
            <table className="blm-table">
              <thead>
                <tr>
                  <th>Printer Name</th>
                  <th>Manufacturer / Model</th>
                  <th>Connection</th>
                  <th>Protocol</th>
                  <th>Windows Spooler Name</th>
                  <th>DPI</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {printers.map((p) => (
                  <tr key={p.printerId}>
                    <td><strong>{p.printerName}</strong></td>
                    <td>{p.manufacturer} {p.model}</td>
                    <td><span className="blm-tag">{p.connectionType}</span></td>
                    <td><span className="blm-symbology-badge">{p.protocol}</span></td>
                    <td className="font-mono text-xs">{p.windowsPrinterName || p.ipAddress || '—'}</td>
                    <td>{p.dpi} DPI</td>
                    <td><span className="blm-badge badge-active">{p.status}</span></td>
                    <td>
                      <div className="blm-actions-cell">
                        <button
                          type="button"
                          className="blm-btn-row-edit"
                          onClick={() => {
                            setEditingPrinter({ ...p });
                            setShowPrinterModal(true);
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          type="button"
                          className="blm-btn-test-action"
                          onClick={async () => {
                            const res = await barcodePrintService.testPrinterConnection(p.printerId);
                            showToast(res.message || 'Connection verified', res.data ? 'success' : 'warning');
                          }}
                        >
                          ⚡ Test
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ========================================================================= */}
      {/* VIEW 4: PRINTER MAPPINGS                                                  */}
      {/* ========================================================================= */}
      {activeView === 'MAPPINGS' && (
        <section className="blm-content-section">
          <div className="blm-controls-bar">
            <h3 className="font-bold text-base">Deterministic Hardware Mappings</h3>
          </div>
          <div className="blm-table-wrap">
            <table className="blm-table">
              <thead>
                <tr>
                  <th>Scope</th>
                  <th>Assigned Template</th>
                  <th>Primary Printer</th>
                  <th>Fallback Printer</th>
                  <th>Policy</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m) => (
                  <tr key={m.mappingId}>
                    <td>{m.departmentName || 'Universal All Departments'}</td>
                    <td><strong>{m.templateName}</strong></td>
                    <td><span className="blm-printer-tag">🖨️ {m.primaryPrinterName}</span></td>
                    <td>{m.fallbackPrinterName ? `🖨️ ${m.fallbackPrinterName}` : '—'}</td>
                    <td><span className="blm-tag">{m.fallbackPolicy}</span></td>
                    <td><span className="blm-badge badge-active">{m.isActive ? 'ACTIVE' : 'INACTIVE'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ========================================================================= */}
      {/* VIEW 5: PRINT JOB AUDIT LOG                                               */}
      {/* ========================================================================= */}
      {activeView === 'AUDIT' && (
        <section className="blm-content-section">
          <div className="blm-controls-bar">
            <h3 className="font-bold text-base">Print Job & Reprint Audit Trail</h3>
            <button
              type="button"
              className="blm-btn-outline"
              onClick={async () => {
                const res = await barcodePrintService.getPrintJobs();
                if (res.data) setAuditLogs(res.data);
                showToast('Audit trail refreshed', 'info');
              }}
            >
              🔄 Refresh
            </button>
          </div>

          <div className="blm-table-wrap">
            <table className="blm-table">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Barcode</th>
                  <th>Patient Name</th>
                  <th>Template</th>
                  <th>Printer</th>
                  <th>Status</th>
                  <th>Reprint?</th>
                  <th>Reprint Reason</th>
                  <th>Printed By</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="blm-empty-td">No print jobs recorded in audit yet.</td>
                  </tr>
                ) : (
                  auditLogs.map((j) => (
                    <tr key={j.jobId}>
                      <td className="font-mono text-xs">#{j.jobId}</td>
                      <td className="font-mono font-bold">{j.barcodeNo}</td>
                      <td>{j.patientName || '—'}</td>
                      <td>{j.templateName} (v{j.templateVersion})</td>
                      <td>{j.printerName} ({j.protocol})</td>
                      <td><span className="blm-badge badge-active">{j.status}</span></td>
                      <td>{j.isReprint ? <span style={{ color: '#d97706', fontWeight: 700 }}>REPRINT</span> : 'Original'}</td>
                      <td className="text-xs text-muted">{j.reprintReason || '—'}</td>
                      <td>{j.printedBy}</td>
                      <td className="text-xs">{new Date(j.printedAt).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* VERSION HISTORY MODAL */}
      {versionModalTemplate && (
        <div className="blm-modal-overlay" onClick={() => setVersionModalTemplate(null)}>
          <div className="blm-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="blm-modal-header">
              <h3 className="font-bold text-base">Version History: {versionModalTemplate.templateName}</h3>
              <button type="button" onClick={() => setVersionModalTemplate(null)}>✕</button>
            </div>
            <div className="blm-modal-body">
              <table className="blm-table">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Change Reason</th>
                    <th>Saved By</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {versionsList.map((v) => (
                    <tr key={v.versionId}>
                      <td><span className="blm-symbology-badge">v{v.versionNumber}</span></td>
                      <td>{v.changeReason || 'Template modification'}</td>
                      <td>{v.createdBy}</td>
                      <td className="text-xs">{new Date(v.createdDate).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* THERMAL PRINTER REGISTRATION MODAL */}
      {showPrinterModal && editingPrinter && (
        <div className="blm-modal-overlay" onClick={() => setShowPrinterModal(false)}>
          <div className="blm-modal-card" style={{ maxWidth: '640px' }} onClick={(e) => e.stopPropagation()}>
            <div className="blm-modal-header">
              <div>
                <h3 className="font-bold text-base" style={{ margin: 0 }}>
                  {editingPrinter.printerId ? '✏️ Edit Thermal Printer' : '🖨️ Register New Thermal Printer'}
                </h3>
                <p className="text-xs text-muted" style={{ margin: '2px 0 0 0' }}>
                  Configure thermal hardware spooler, communication protocol and Windows driver queue.
                </p>
              </div>
              <button type="button" className="blm-modal-close" onClick={() => setShowPrinterModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSavePrinter}>
              <div className="blm-modal-body">
                <div className="blm-modal-grid">
                  <div className="blm-modal-field" style={{ gridColumn: 'span 2' }}>
                    <label>Friendly Printer Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Phlebotomy Desk 1 - TSC / Reception Zebra"
                      value={editingPrinter.printerName || ''}
                      onChange={(e) => setEditingPrinter({ ...editingPrinter, printerName: e.target.value })}
                    />
                  </div>

                  <div className="blm-modal-field">
                    <label>Manufacturer</label>
                    <select
                      value={editingPrinter.manufacturer || 'Zebra'}
                      onChange={(e) => setEditingPrinter({ ...editingPrinter, manufacturer: e.target.value })}
                    >
                      <option value="Zebra">Zebra (ZDesigner)</option>
                      <option value="TSC">TSC Auto ID</option>
                      <option value="TVS">TVS Electronics</option>
                      <option value="Godex">Godex</option>
                      <option value="Citizen">Citizen</option>
                      <option value="Other">Other Thermal</option>
                    </select>
                  </div>

                  <div className="blm-modal-field">
                    <label>Model</label>
                    <input
                      type="text"
                      placeholder="e.g. ZD220 / TE244 / LP 46"
                      value={editingPrinter.model || ''}
                      onChange={(e) => setEditingPrinter({ ...editingPrinter, model: e.target.value })}
                    />
                  </div>

                  <div className="blm-modal-field">
                    <label>Connection Architecture</label>
                    <select
                      value={editingPrinter.connectionType || 'LOCAL_AGENT'}
                      onChange={(e) => setEditingPrinter({ ...editingPrinter, connectionType: e.target.value })}
                    >
                      <option value="LOCAL_AGENT">LOCAL_AGENT (Direct Silent USB Print)</option>
                      <option value="NETWORK_TCP">NETWORK_TCP (Raw Socket LAN / IP)</option>
                      <option value="BROWSER_PRINT">BROWSER_PRINT (Standard Print Spooler)</option>
                    </select>
                  </div>

                  <div className="blm-modal-field">
                    <label>Thermal Protocol</label>
                    <select
                      value={editingPrinter.protocol || 'ZPL'}
                      onChange={(e) => setEditingPrinter({ ...editingPrinter, protocol: e.target.value })}
                    >
                      <option value="ZPL">ZPL (Zebra - Gold Standard)</option>
                      <option value="TSPL">TSPL (TSC, TVS, Godex)</option>
                      <option value="EPL">EPL (Eltron / Legacy)</option>
                      <option value="ESC_POS">ESC/POS (Thermal Receipt/Slip)</option>
                    </select>
                  </div>

                  <div className="blm-modal-field" style={{ gridColumn: 'span 2' }}>
                    <label>Windows Spooler Name (Exact name in Control Panel) *</label>
                    <input
                      type="text"
                      placeholder="e.g. TSC TE244 or ZDesigner ZD220-203dpi ZPL"
                      value={editingPrinter.windowsPrinterName || ''}
                      onChange={(e) => setEditingPrinter({ ...editingPrinter, windowsPrinterName: e.target.value })}
                    />
                    <small className="text-muted" style={{ fontSize: '0.72rem', marginTop: '3px', display: 'block' }}>
                      Windows ke <em>"Printers & Scanners"</em> me jo printer ka naam likha hai, exact wahi yahan likhein.
                    </small>
                  </div>

                  {editingPrinter.connectionType === 'NETWORK_TCP' && (
                    <>
                      <div className="blm-modal-field">
                        <label>LAN IP Address</label>
                        <input
                          type="text"
                          placeholder="192.168.1.100"
                          value={editingPrinter.ipAddress || ''}
                          onChange={(e) => setEditingPrinter({ ...editingPrinter, ipAddress: e.target.value })}
                        />
                      </div>
                      <div className="blm-modal-field">
                        <label>Raw Port</label>
                        <input
                          type="number"
                          placeholder="9100"
                          value={editingPrinter.port || 9100}
                          onChange={(e) => setEditingPrinter({ ...editingPrinter, port: parseInt(e.target.value, 10) })}
                        />
                      </div>
                    </>
                  )}

                  <div className="blm-modal-field">
                    <label>Printer Resolution</label>
                    <select
                      value={editingPrinter.dpi || 203}
                      onChange={(e) => setEditingPrinter({ ...editingPrinter, dpi: parseInt(e.target.value, 10) })}
                    >
                      <option value={203}>203 DPI (Standard Lab Label)</option>
                      <option value={300}>300 DPI (High Definition Micro Barcodes)</option>
                    </select>
                  </div>

                  <div className="blm-modal-field" style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingTop: '22px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0, fontSize: '0.8rem' }}>
                      <input
                        type="checkbox"
                        checked={!!editingPrinter.isDefault}
                        onChange={(e) => setEditingPrinter({ ...editingPrinter, isDefault: e.target.checked })}
                      />
                      Default Printer
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0, fontSize: '0.8rem' }}>
                      <input
                        type="checkbox"
                        checked={!!editingPrinter.isActive}
                        onChange={(e) => setEditingPrinter({ ...editingPrinter, isActive: e.target.checked })}
                      />
                      Active Hardware
                    </label>
                  </div>
                </div>
              </div>
              <div className="blm-modal-footer">
                <button type="button" className="blm-btn-cancel" onClick={() => setShowPrinterModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="blm-btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : '💾 Save Printer Configuration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BarcodeLabelMasterPage;
